import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@eon/utils';

/**
 * Checkbox is a styled wrapper over Radix Checkbox. Supports tri-state via
 * `checked === 'indeterminate'`. Use the higher-level <CheckboxField> when
 * you need a label that toggles the checkbox on click.
 */
export const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer border-input bg-background ring-offset-background h-4 w-4 shrink-0 rounded-sm border',
      'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary',
      'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center">
      {props.checked === 'indeterminate' ? (
        <Minus className="h-3 w-3" strokeWidth={3} />
      ) : (
        <Check className="h-3 w-3" strokeWidth={3} />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = 'Checkbox';

export interface CheckboxFieldProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Inline label rendered to the right of the checkbox. */
  label: ReactNode;
  /** Optional helper text shown below the label. */
  description?: ReactNode;
  disabled?: boolean;
  /** Render as a full-width "card-style" row. */
  variant?: 'inline' | 'card';
  className?: string;
}

/**
 * CheckboxField is a Checkbox + label combo. The whole row is clickable and
 * keyboard-navigable. Two visual variants:
 *   - `inline` (default): tight row, suitable for grids
 *   - `card`: padded surface with hover/focus highlight, suitable for opt-in lists
 */
export const CheckboxField = forwardRef<HTMLLabelElement, CheckboxFieldProps>(
  ({ checked, onCheckedChange, label, description, disabled, variant = 'inline', className }, ref) => {
    return (
      <label
        ref={ref}
        className={cn(
          variant === 'card'
            ? 'bg-muted/30 hover:bg-muted/50 has-[:focus-visible]:ring-ring flex cursor-pointer items-start gap-3 rounded-md p-3 has-[:focus-visible]:ring-2'
            : 'inline-flex cursor-pointer items-center gap-2',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        <Checkbox checked={checked} onCheckedChange={(c) => onCheckedChange(c === true)} disabled={disabled} />
        <span className="flex flex-col">
          <span className="text-sm font-medium leading-none">{label}</span>
          {description ? <span className="text-muted-foreground mt-1 text-xs">{description}</span> : null}
        </span>
      </label>
    );
  },
);
CheckboxField.displayName = 'CheckboxField';
